import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, Stack, Chip, Divider } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CodeIcon from '@mui/icons-material/Code';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { Reveal } from '../landing/motion';
import { SiteNav, SiteFooter, SectionHead, CtaBlock } from '../landing/components';
import { FEATURES, STEPS, STATS, CREDENTIALS, PILLARS, FAQS } from '../landing/data';
import ComplianceChecklist from '../landing/ComplianceChecklist';
import PenaltyCalculator from '../landing/PenaltyCalculator';
import FaqSection from '../landing/FaqSection';
import Testimonials from '../landing/Testimonials';
import { PRICING_ENABLED } from '../utils';
import {
  ORANGE,
  ORANGE_DK,
  ORANGE_SOFT,
  GREEN,
  RED,
  BG,
  BG_SOFT,
  BORDER,
  TXT,
  TXT_DIM,
  FONT_STACK,
  gradientText,
  card,
  primaryBtn,
  ghostBtn,
} from '../landing/theme';

/* Restrained background — single orange glow + engineered grid */
function HeroBackdrop() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        '@keyframes drift': {
          '0%,100%': { transform: 'translate(0,0) scale(1)' },
          '50%': { transform: 'translate(40px,-30px) scale(1.1)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${BORDER} 1px, transparent 1px), linear-gradient(90deg, ${BORDER} 1px, transparent 1px)`,
          backgroundSize: '44px 44px',
          opacity: 0.5,
          maskImage: 'radial-gradient(ellipse 78% 60% at 50% 22%, #000 30%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse 78% 60% at 50% 22%, #000 30%, transparent 72%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '-16%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 620,
          height: 520,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${ORANGE} 0%, transparent 64%)`,
          filter: 'blur(120px)',
          opacity: 0.14,
          animation: 'drift 18s ease-in-out infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
}

/* Centered "view the standalone page" deep link. */
function MoreLink({ label, onClick }) {
  return (
    <Box sx={{ textAlign: 'center', mt: 3.5 }}>
      <Button onClick={onClick} endIcon={<ArrowForwardIcon />} sx={{ ...ghostBtn, px: 3 }}>
        {label}
      </Button>
    </Box>
  );
}

function LandingPage() {
  const navigate = useNavigate();

  const navItems = [
    { label: 'Platform', href: '#features' },
    { label: 'Checklist', href: '#checklist' },
    { label: 'Risk estimator', href: '#calculator' },
    { label: 'FAQ', href: '#faq' },
    ...(PRICING_ENABLED ? [{ label: 'Pricing', onClick: () => navigate('/pricing') }] : []),
    { label: 'Developers', href: '#developers' },
  ];

  return (
    <Box sx={{ bgcolor: BG, color: TXT, overflowX: 'hidden', fontFamily: FONT_STACK }}>
      <SiteNav navItems={navItems} />

      {/* ---------------- Hero ---------------- */}
      <Box sx={{ position: 'relative' }}>
        <HeroBackdrop />
        <Container maxWidth="lg" sx={{ position: 'relative', pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
              gap: { xs: 4, md: 6 },
              alignItems: 'center',
            }}
          >
            {/* Left: copy */}
            <Reveal>
              <Stack spacing={2.5} sx={{ textAlign: { xs: 'center', md: 'left' }, alignItems: { xs: 'center', md: 'flex-start' } }}>
                <Chip
                  icon={<VerifiedOutlinedIcon sx={{ color: `${ORANGE_DK} !important`, fontSize: 16 }} />}
                  label="DPDP-ready, multi-tenant consent infrastructure for India"
                  sx={{
                    px: 1,
                    py: 2.2,
                    color: ORANGE_DK,
                    fontWeight: 600,
                    borderRadius: 1.5,
                    background: ORANGE_SOFT,
                    border: `1px solid rgba(255,102,0,0.24)`,
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -1.5,
                    lineHeight: 1.04,
                    fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.6rem' },
                  }}
                >
                  Consent, captured.
                  <br />
                  <Box component="span" sx={gradientText}>
                    DPDP, proven.
                  </Box>
                </Typography>
                <Typography sx={{ color: TXT_DIM, fontSize: { xs: '1.02rem', md: '1.15rem' }, maxWidth: 520, lineHeight: 1.6 }}>
                  Collect, manage, and audit Data Principal consent across every purpose and channel — backed by an
                  immutable trail built for India’s Digital Personal Data Protection Act, 2023. Notices, consent, and proof in one place.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 0.5 }}>
                  <Button size="large" sx={primaryBtn} endIcon={<ArrowForwardIcon />} onClick={() => navigate('/register')}>
                    Start free
                  </Button>
                  <Button size="large" sx={ghostBtn} startIcon={<CodeIcon />} onClick={() => navigate('/documentation/api')}>
                    Read the API docs
                  </Button>
                </Stack>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, pt: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  {CREDENTIALS.slice(0, 4).map((c) => (
                    <Stack
                      key={c}
                      direction="row"
                      alignItems="center"
                      spacing={0.6}
                      sx={{ px: 1.4, py: 0.6, borderRadius: 1.5, background: BG_SOFT, border: `1px solid ${BORDER}` }}
                    >
                      <CheckRoundedIcon sx={{ fontSize: 15, color: GREEN }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TXT_DIM }}>{c}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Reveal>

            {/* Right: consent notice preview */}
            <Reveal delay={150} y={32}>
              <Box sx={{ p: { xs: 2, md: 2.5 }, ...card, boxShadow: '0 24px 60px rgba(255,102,0,0.12), 0 6px 24px rgba(15,23,42,0.08)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, pb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FactCheckOutlinedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Consent Notice — Marketing</Typography>
                  </Stack>
                  <Chip size="small" label="● Published" sx={{ color: GREEN, background: 'rgba(4,120,87,0.1)', fontWeight: 600, fontSize: '0.7rem' }} />
                </Stack>
                <Divider sx={{ borderColor: BORDER }} />
                <Stack spacing={1.2} sx={{ pt: 1.5 }}>
                  {[
                    { name: 'Marketing Emails', pii: 'email', on: true },
                    { name: 'Product Analytics', pii: 'usage data', on: true },
                    { name: 'Third-party Sharing', pii: 'profile', on: false },
                  ].map((r) => (
                    <Stack
                      key={r.name}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ px: 2, py: 1.3, borderRadius: 2, background: BG_SOFT, border: `1px solid ${BORDER}` }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.name}</Typography>
                        <Typography sx={{ color: TXT_DIM, fontSize: '0.72rem' }}>PII · {r.pii}</Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={r.on ? 'Accepted' : 'Rejected'}
                        sx={{ fontWeight: 700, fontSize: '0.7rem', color: r.on ? GREEN : RED, background: r.on ? 'rgba(4,120,87,0.1)' : 'rgba(220,38,38,0.08)' }}
                      />
                    </Stack>
                  ))}
                </Stack>
                <Box sx={{ mt: 1.5, px: 2, py: 1.2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, background: ORANGE_SOFT, border: `1px solid rgba(255,102,0,0.2)` }}>
                  <BoltOutlinedIcon sx={{ color: ORANGE_DK, fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TXT }}>
                    Written to immutable audit trail · POST /consent/push → 201
                  </Typography>
                </Box>
              </Box>
            </Reveal>
          </Box>
        </Container>
      </Box>

      {/* ---------------- Trust / credentials band ---------------- */}
      <Box sx={{ bgcolor: BG_SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 3.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems="center" justifyContent="space-between">
            <Typography sx={{ color: TXT_DIM, fontWeight: 700, letterSpacing: 1.2, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
              BUILT TO COMPLY WITH
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {CREDENTIALS.map((c) => (
                <Stack key={c} direction="row" alignItems="center" spacing={0.6} sx={{ px: 1.5, py: 0.7, borderRadius: 1.5, background: '#fff', border: `1px solid ${BORDER}` }}>
                  <ShieldOutlinedIcon sx={{ fontSize: 15, color: ORANGE }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: TXT }}>{c}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ---------------- Authority pillars ---------------- */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2.5 }}>
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 80}>
              <Box sx={{ ...card, p: { xs: 3, md: 3.5 }, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', color: ORANGE_DK, background: ORANGE_SOFT, border: `1px solid rgba(255,102,0,0.2)` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: TXT }}>{p.kpi}</Typography>
                </Stack>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.6 }}>{p.title}</Typography>
                <Typography sx={{ color: TXT_DIM, fontSize: '0.9rem', lineHeight: 1.6 }}>{p.desc}</Typography>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Container>

      {/* ---------------- Bento features ---------------- */}
      <Box id="features" sx={{ bgcolor: BG_SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Reveal>
            <SectionHead overline="The platform" title="A complete consent lifecycle, in one surface" />
          </Reveal>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 2.5 }}>
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 50}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    ...card,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { transform: 'translateY(-4px)', borderColor: 'rgba(255,102,0,0.4)', boxShadow: '0 18px 40px rgba(255,102,0,0.1)' },
                  }}
                >
                  <Box sx={{ width: 46, height: 46, borderRadius: 2, display: 'grid', placeItems: 'center', mb: 2, color: ORANGE_DK, background: ORANGE_SOFT, border: `1px solid rgba(255,102,0,0.2)` }}>
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.8 }}>{f.title}</Typography>
                  <Typography sx={{ color: TXT_DIM, fontSize: '0.9rem', lineHeight: 1.55 }}>{f.desc}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ---------------- How it works ---------------- */}
      <Box id="how">
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Reveal>
            <SectionHead overline="How it works" title="From purpose to proof in four steps" />
          </Reveal>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 2.5 }}>
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 80}>
                <Box sx={{ p: 3, height: '100%', ...card }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '2.4rem', lineHeight: 1, mb: 1.5, ...gradientText }}>{s.num}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.8 }}>{s.title}</Typography>
                  <Typography sx={{ color: TXT_DIM, fontSize: '0.88rem', lineHeight: 1.55 }}>{s.desc}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ---------------- Compliance checklist ---------------- */}
      <Box id="checklist" sx={{ bgcolor: BG_SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Reveal>
            <SectionHead overline="Free self-assessment" title="How DPDP-ready are you?" />
            <Typography sx={{ color: TXT_DIM, fontSize: '1rem', textAlign: 'center', maxWidth: 560, mx: 'auto', mt: -2, mb: { xs: 4, md: 5 } }}>
              Tick what you already have in place and watch your readiness score update live.
            </Typography>
          </Reveal>
          <Reveal delay={100}>
            <ComplianceChecklist />
          </Reveal>
          <MoreLink label="Open the full readiness checklist" onClick={() => navigate('/dpdp-readiness-checklist')} />
        </Container>
      </Box>

      {/* ---------------- Penalty calculator ---------------- */}
      <Box id="calculator">
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Reveal>
            <SectionHead overline="Free risk estimator" title="What could non-compliance cost you?" />
            <Typography sx={{ color: TXT_DIM, fontSize: '1rem', textAlign: 'center', maxWidth: 580, mx: 'auto', mt: -2, mb: { xs: 4, md: 5 } }}>
              See the statutory maximum penalty under India’s Digital Personal Data Protection Act, 2023 for each type of breach.
            </Typography>
          </Reveal>
          <Reveal delay={100}>
            <PenaltyCalculator />
          </Reveal>
          <MoreLink label="Open the full penalty calculator" onClick={() => navigate('/dpdp-penalty-calculator')} />
        </Container>
      </Box>

      {/* ---------------- FAQ (teaser) ---------------- */}
      <Box id="faq" sx={{ bgcolor: BG_SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Reveal>
            <SectionHead overline="FAQ" title="DPDP, answered" />
            <Typography sx={{ color: TXT_DIM, fontSize: '1rem', textAlign: 'center', maxWidth: 580, mx: 'auto', mt: -2, mb: { xs: 4, md: 5 } }}>
              Plain-language answers on consent, Data Principal rights, and your obligations under the DPDP Act, 2023.
            </Typography>
          </Reveal>
          <FaqSection items={FAQS} />
        </Container>
      </Box>

      {/* ---------------- Developers ---------------- */}
      <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <Container id="developers" maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 7 }, alignItems: 'center' }}>
            <Reveal>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CodeIcon sx={{ color: ORANGE_DK }} />
                <Typography sx={{ color: ORANGE_DK, fontWeight: 700, letterSpacing: 1.5, fontSize: '0.78rem' }}>BUILT FOR DEVELOPERS</Typography>
              </Stack>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, letterSpacing: -0.8, mb: 2 }}>
                A clean REST API for every consent action
              </Typography>
              <Typography sx={{ color: TXT_DIM, fontSize: '1rem', lineHeight: 1.6, mb: 3 }}>
                Push consent from any app in a single call. JWT-secured endpoints, predictable JSON, and full
                audit-log plus CSV / JSON export APIs for your compliance team.
              </Typography>
              <Stack spacing={1.4}>
                {[
                  'POST /consent/push — record notice + purposes array',
                  'GET /consent/retrieve-user-consent — fetch a Data Principal’s state',
                  'GET /audit-logs — paginated, immutable records',
                  'GET /export/consents — export everything as CSV or JSON',
                ].map((line) => (
                  <Stack key={line} direction="row" spacing={1.2} alignItems="flex-start">
                    <CheckRoundedIcon sx={{ mt: '2px', fontSize: 18, color: ORANGE_DK, flexShrink: 0 }} />
                    <Typography sx={{ color: TXT, fontSize: '0.92rem' }}>{line}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Button sx={{ ...primaryBtn, mt: 4 }} endIcon={<ArrowForwardIcon />} onClick={() => navigate('/documentation/api')}>
                Explore the API
              </Button>
            </Reveal>

            <Reveal delay={120}>
              <Box sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative', p: '1px', background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DK})`, boxShadow: '0 24px 60px rgba(255,102,0,0.16)' }}>
                <Box sx={{ borderRadius: '11px', overflow: 'hidden', background: '#0B0D16' }}>
                  <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.4, background: '#11131F' }}>
                    {['#FF5F56', '#FFBD2E', '#27C93F'].map((c) => (
                      <Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', background: c }} />
                    ))}
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', ml: 1 }}>push-consent.sh</Typography>
                  </Stack>
                  <Box
                    component="pre"
                    sx={{ m: 0, p: 3, fontSize: '0.78rem', lineHeight: 1.75, color: '#C9D1E3', fontFamily: 'Menlo, Monaco, "Courier New", monospace', overflowX: 'auto' }}
                  >
{`curl -X POST https://your-domain.com/api/consent/push \\
  -H "Authorization: Bearer <jwt>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "notice_id": "notice_8f3a",
    "purposes": [
      { "name": "Analytics", "status": "accepted" },
      { "name": "Marketing", "status": "rejected" }
    ],
    "details": "Captured from cookie banner"
  }'

# → 201 Created
# { "message": "Consent recorded",
#   "data": { "log_id": "...", "audited": true } }`}
                  </Box>
                </Box>
              </Box>
            </Reveal>
          </Box>
        </Container>
      </Box>

      {/* ---------------- Stats ---------------- */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Reveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
              gap: 2.5,
              p: { xs: 3, md: 5 },
              ...card,
              background: 'linear-gradient(135deg, rgba(255,102,0,0.05), rgba(255,102,0,0.02))',
            }}
          >
            {STATS.map((s) => (
              <Box key={s.l} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '2.2rem', md: '2.8rem' }, ...gradientText }}>{s.v}</Typography>
                <Typography sx={{ color: TXT_DIM, fontSize: '0.85rem' }}>{s.l}</Typography>
              </Box>
            ))}
          </Box>
        </Reveal>
      </Container>

      {/* ---------------- Testimonials (admin-managed; hidden when empty) ---------------- */}
      <Testimonials />

      {/* ---------------- Final CTA ---------------- */}
      <CtaBlock
        title="Make consent your"
        highlight="advantage"
        subtitle="Stand up your first DPDP consent notice in under 10 minutes."
        primaryLabel="Start for free"
        secondaryLabel="Sign in"
      />

      <SiteFooter />
    </Box>
  );
}

export default LandingPage;
